/**
 * useSmileVerify — Continuity System Phase 3.
 *
 * Encapsulates the side-effects of completing a Smile ID capture flow:
 *   1. Convert the two base64 images (selfie + ID) to multipart Files.
 *   2. Upload both to /api/successor/claim/{token}/upload (one per doc_type).
 *   3. Surface a single loading state + per-step toasts.
 *
 * The backend automatically runs Smile ID Document Verification once both
 * docs are persisted (see estate_vault_successor.py::_run_advisory_smile_id_if_gov_id).
 */
import { useState, useCallback } from 'react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const b64ToFile = (b64, filename, mime = 'image/jpeg') => {
  const clean = b64.includes(',') ? b64.split(',')[1] : b64;
  const bin = atob(clean);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return new File([bytes], filename, { type: mime });
};

const uploadOne = async (token, docType, file) => {
  const fd = new FormData();
  fd.append('doc_type', docType);
  fd.append('file', file);
  const res = await fetch(`${BACKEND_URL}/api/successor/claim/${token}/upload`, {
    method: 'POST', body: fd,
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j.detail || `Upload of ${docType} failed.`);
  }
};

export const useSmileVerify = (token, onComplete) => {
  const [verifying, setVerifying] = useState(false);

  const submit = useCallback(async ({ selfieB64, idB64 }) => {
    setVerifying(true);
    try {
      const selfieFile = b64ToFile(selfieB64, 'selfie.jpg');
      const idFile = b64ToFile(idB64, 'id.jpg');
      await uploadOne(token, 'gov_id', idFile);
      await uploadOne(token, 'selfie', selfieFile);
      toast.success('Identity capture uploaded. Our team will be notified once the automated check completes.');
      if (onComplete) await onComplete();
    } catch (err) {
      toast.error(err.message || 'Identity capture failed. Please try again.');
    } finally {
      setVerifying(false);
    }
  }, [token, onComplete]);

  return { verifying, submit };
};

export default useSmileVerify;
